import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CatIconInfo } from "blocks-nextjs/blocks-react/components/Icons/Info/Info";
import Plotly from "plotly.js-dist-min";
import React, { useEffect, useRef } from "react";
/**
 * Cycle Time visualisation for the Energy &amp; Productivity panel. Renders a
 * Plotly donut chart on the left, a colour-coded legend in the middle, and
 * an activity table on the right.
 */
const EnergyProductivityDonutChart = ({ series }) => {
    const containerRef = useRef(null);
    const LEGEND_SWATCH_CLASSES = {
        Carry: "bg-[#FFA230]",
        Charging: "bg-[#0DB3A0]",
        Idle: "bg-[#86BAFF]",
        Load: "bg-[#777777]"
    };
    const LEGEND_ORDER = ["Load", "Carry", "Charging", "Idle"];
    useEffect(() => {
        const node = containerRef.current;
        if (!node)
            return undefined;
        const trace = {
            direction: "clockwise",
            hole: 0.75,
            hoverinfo: "label+percent",
            hoverlabel: {
                font: {
                    color: "#FFFFFF"
                }
            },
            labels: series.slices.map((slice) => slice.label),
            marker: {
                colors: series.slices.map((slice) => slice.color),
                line: { color: "#ffffff", width: 2 }
            },
            rotation: -90,
            sort: false,
            textinfo: "none",
            type: "pie",
            values: series.slices.map((slice) => slice.value)
        };
        const layout = {
            margin: { b: 10, l: 10, r: 10, t: 0 },
            paper_bgcolor: "transparent",
            plot_bgcolor: "transparent",
            showlegend: false
        };
        const config = { displayModeBar: false, responsive: true };
        Plotly.newPlot(node, [trace], layout, config);
        const handleResize = () => {
            Plotly.Plots.resize(node);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            Plotly.purge(node);
        };
    }, [series]);
    return (_jsxs("div", { className: "w-full flex flex-col pl-4", "data-test": "energy-productivity-donut", children: [_jsxs("div", { className: "flex flex-row items-center", children: [_jsx("div", { className: "flex-shrink-0 w-[250px] h-[250px]", children: _jsx("div", { className: "energy-productivity-donut-canvas w-full h-full", "data-test": "energy-productivity-donut-canvas", ref: containerRef }) }), _jsxs("div", { className: "flex flex-col gap-2 pl-4 text-sm text-gray-800", "data-test": "energy-productivity-donut-legend", children: [_jsx("span", { className: "text-xs text-gray-500 mb-1", children: "Legend" }), LEGEND_ORDER.map((label) => (_jsxs("div", { className: "flex items-center gap-2", "data-test": `energy-productivity-donut-legend-${label.toLowerCase()}`, children: [_jsx("span", { "aria-hidden": "true", className: `inline-block w-3 h-3 rounded-full flex-shrink-0 ${LEGEND_SWATCH_CLASSES[label]}` }), _jsx("span", { children: label })] }, label)))] })] }), _jsxs("table", { className: "energy-productivity-activity-table w-full text-sm mt-2", "data-test": "energy-productivity-activity-table", children: [_jsx("thead", { children: _jsxs("tr", { className: "border-b text-gray-700", children: [_jsx("th", { className: "py-2 font-semibold text-left", scope: "col", children: "Activity" }), _jsx("th", { className: "py-2 font-semibold text-right", scope: "col", children: _jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx("span", { children: "Time" }), _jsx(CatIconInfo, { "aria-label": "Time spent in each activity within the selected Cycle Time view", className: "inline-flex justify-center w-4 h-4 text-gray-500", "data-test": "energy-productivity-activity-info", iconTitle: "Time spent in each activity within the selected Cycle Time view", title: "Time spent in each activity within the selected Cycle Time view" })] }) })] }) }), _jsx("tbody", { children: series.activities.map((row) => (_jsxs("tr", { className: "border-b last:border-b-0", "data-test": `energy-productivity-activity-row-${row.label.toLowerCase()}`, children: [_jsx("td", { className: "py-3", children: _jsxs("span", { className: "inline-flex items-center gap-1 font-semibold", children: [_jsx("span", { children: row.label }), _jsx(CatIconInfo, { "aria-label": `${row.label} duration in the selected Cycle Time view`, className: "inline-flex justify-center w-4 h-4 text-gray-500 font-normal", iconTitle: `${row.label} duration in the selected Cycle Time view`, title: `${row.label} duration in the selected Cycle Time view` })] }) }), _jsx("td", { className: "py-3 text-right", children: row.timeLabel })] }, row.label))) })] })] }));
};
export default EnergyProductivityDonutChart;
